import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

import {
  TELEGRAM_EVENTS,
  getTelegramSettings,
  sendTelegramTest,
  updateTelegramSettings,
  type TelegramSettings,
  type TelegramSettingsPatch,
} from '@/api/telegram_settings'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * /telegram-settings — Telegram-оповещения команды по Авито-лидам.
 *
 * Источник данных: единственная строка `public.telegram_settings` (id=1). Read
 * через RPC `get_telegram_settings`, write через `update_telegram_settings(jsonb)`
 * под admin. Токен бота тут не хранится (он в vault) — показываем только
 * @username. Канал шлёт сообщения в группу команды через бота.
 *
 * Боевой сервер режет api.telegram.org напрямую, поэтому отправка идёт через
 * SOCKS5-прокси (настроен на бэке, в UI не виден).
 */
export function TelegramLeadsSettingsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['telegram-settings'],
    queryFn: getTelegramSettings,
    staleTime: 30_000,
  })

  const [draft, setDraft] = useState<TelegramSettingsPatch>({})
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [testMsg, setTestMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (data) setDraft({})
  }, [data])

  const mut = useMutation({
    mutationFn: (patch: TelegramSettingsPatch) => updateTelegramSettings(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['telegram-settings'] })
      setDraft({})
      setErrorMsg(null)
    },
    onError: (e: Error) => setErrorMsg(e.message),
  })

  const testMut = useMutation({
    mutationFn: sendTelegramTest,
    onSuccess: () => setTestMsg({ kind: 'ok', text: t('settings.telegram.testSent') }),
    onError: (e: Error) => setTestMsg({ kind: 'error', text: e.message }),
  })

  const merged = useMemo<TelegramSettings | null>(() => {
    if (!data) return null
    return { ...data, ...draft }
  }, [data, draft])

  const isDirty = Object.keys(draft).length > 0

  function update<K extends keyof TelegramSettingsPatch>(
    key: K,
    value: TelegramSettingsPatch[K],
  ) {
    setDraft((prev) => {
      const next = { ...prev, [key]: value }
      if (data && next[key] === (data as TelegramSettings)[key]) {
        delete next[key]
      }
      return next
    })
  }

  function discard() {
    setDraft({})
    setErrorMsg(null)
  }

  function save() {
    if (!isDirty) return
    mut.mutate(draft)
  }

  return (
    <div>
      <PageHeader
        title={t('settings.telegram.title')}
        subtitle={t('settings.telegram.subtitle')}
      />

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Banner kind="error" text={t('settings.error', { message: (error as Error).message })} />
      ) : !data || !merged ? (
        <Banner kind="muted" text={t('settings.notLoaded')} />
      ) : (
        <div className="flex flex-col gap-6">
          <Section title={t('settings.telegram.channel.title')} hint={t('settings.telegram.channel.hint')}>
            <div className="flex flex-col gap-4">
              <ToggleField
                label={t('settings.telegram.channel.enabled')}
                value={merged.enabled}
                onChange={(v) => update('enabled', v)}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ReadField label={t('settings.telegram.bot')} value={merged.bot_username ? `@${merged.bot_username}` : t('settings.telegram.botNotConfigured')} />
                <TextField
                  label={t('settings.telegram.chatId')}
                  value={merged.chat_id ?? ''}
                  placeholder="-100…"
                  onChange={(v) => update('chat_id', v)}
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setTestMsg(null); testMut.mutate() }}
                  disabled={testMut.isPending || isDirty}
                  title={isDirty ? t('settings.telegram.saveFirst') : t('settings.telegram.sendTestTitle')}
                  className="rounded border border-[color:var(--stryzh-color-border-default)] px-4 py-2 text-sm text-[color:var(--stryzh-color-text-secondary)] hover:bg-white/5 disabled:opacity-40"
                >
                  {testMut.isPending ? t('settings.telegram.sending') : t('settings.telegram.sendTest')}
                </button>
                {testMsg ? (
                  <span className={testMsg.kind === 'ok' ? 'text-xs text-green-300' : 'text-xs text-red-300'}>
                    {testMsg.text}
                  </span>
                ) : null}
              </div>
            </div>
          </Section>

          <Section title={t('settings.telegram.events.title')} hint={t('settings.telegram.events.hint')}>
            <div className="flex flex-col gap-3">
              {TELEGRAM_EVENTS.map((ev) => (
                <ToggleField
                  key={ev.key}
                  label={ev.label}
                  hint={ev.hint}
                  value={Boolean(merged[ev.key as keyof TelegramSettings])}
                  onChange={(v) => update(ev.key, v)}
                />
              ))}
            </div>
          </Section>

          <Section
            title={t('settings.telegram.stale.title')}
            hint={t('settings.telegram.stale.hint')}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <NumberField
                label={t('settings.telegram.stale.newMinutes')}
                value={merged.stale_new_minutes ?? 0}
                min={0}
                max={10080}
                onChange={(v) => update('stale_new_minutes', v)}
              />
              <NumberField
                label={t('settings.telegram.stale.awaitingMinutes')}
                value={merged.stale_awaiting_minutes ?? 0}
                min={0}
                max={43200}
                onChange={(v) => update('stale_awaiting_minutes', v)}
              />
            </div>
          </Section>

          <Section
            title={t('settings.telegram.repeat.title')}
            hint={t('settings.telegram.repeat.hint')}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <NumberField
                label={t('settings.telegram.repeat.minutes')}
                value={merged.stale_new_repeat_minutes ?? 30}
                min={1}
                max={1440}
                onChange={(v) => update('stale_new_repeat_minutes', v)}
              />
              <NumberField
                label={t('settings.telegram.repeat.windowStart')}
                value={merged.stale_work_start_hour ?? 9}
                min={0}
                max={23}
                onChange={(v) => update('stale_work_start_hour', v)}
              />
              <NumberField
                label={t('settings.telegram.repeat.windowEnd')}
                value={merged.stale_work_end_hour ?? 19}
                min={1}
                max={24}
                onChange={(v) => update('stale_work_end_hour', v)}
              />
            </div>
          </Section>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={!isDirty || mut.isPending}
              className="rounded bg-[color:var(--stryzh-color-accent-orange)] px-4 py-2 text-sm font-semibold text-black transition disabled:opacity-40"
            >
              {mut.isPending ? t('settings.saving') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={discard}
              disabled={!isDirty || mut.isPending}
              className="rounded border border-[color:var(--stryzh-color-border-default)] px-4 py-2 text-sm text-[color:var(--stryzh-color-text-secondary)] hover:bg-white/5 disabled:opacity-40"
            >
              {t('common.cancel')}
            </button>
            <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t('settings.telegram.lastChange', {
                when: formatDistanceToNow(new Date(data.updated_at), { locale: dateFnsLocale(), addSuffix: true }),
              })}
            </div>
          </div>

          {errorMsg ? <Banner kind="error" text={errorMsg} /> : null}
        </div>
      )}
    </div>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
      <div className="mb-3">
        <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
          {title}
        </div>
        {hint ? (
          <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">{hint}</div>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function ToggleField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint?: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-[color:var(--stryzh-color-accent-orange)]"
      />
      <span className="flex flex-col">
        <span className="text-sm text-[color:var(--stryzh-color-text-primary)]">{label}</span>
        {hint ? (
          <span className="text-[11px] text-[color:var(--stryzh-color-text-secondary)]">{hint}</span>
        ) : null}
      </span>
    </label>
  )
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </span>
      <div className="rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)]">
        {value}
      </div>
    </label>
  )
}

function TextField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string
  value: string
  placeholder?: string
  onChange: (v: string) => void
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)]"
      />
    </label>
  )
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  onChange: (v: number) => void
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)))
        }}
        className="rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)]"
      />
      <span className="text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
        {min}—{max}
      </span>
    </label>
  )
}

function Banner({ kind, text }: { kind: 'error' | 'muted'; text: string }) {
  const cls =
    kind === 'error'
      ? 'border-red-500/40 bg-red-500/10 text-red-200'
      : 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]'
  return <div className={`rounded border p-4 text-sm ${cls}`}>{text}</div>
}
